import { getMediaPlaybackRate, shouldLoopMedia } from '../../common';
import { roundToPrecision } from '../../libs/frameUtils';
import { TIMELINE_EVENT_PRECISION } from '../../libs/BaseTimeline';
import { assertMinimum, assertMaximum, assertNumber } from '../../validators';
import type { DestroyOptions } from '../../types';
import { Config } from '../../video-data/config';
import { StringAttribute } from '../attributes';
import { MorphManager, type MorphableProperty } from '../morph/MorphManager';
import { comparer, computed, observable, reaction, untracked } from '../reactivity';
import type { BaseTimeline } from '../../libs/BaseTimeline';
import type { VideoFramesProvider } from '../video/VideoFramesProvider';
import { MediaAsset } from './MediaAsset';

type SyncSchedule = { timeline: BaseTimeline | null; times: readonly number[] };

/**
 * The TimeAwareMediaAsset extends the MediaAsset object, adding in support for time-sensitive properties. These
 * notably are useful for video assets, including startTime, endTime, and volume.
 */
export class TimeAwareMediaAsset extends MediaAsset {
    override _classType = 'TimeAwareMediaAsset'; // should match VideoData.MEDIA_ASSET_TYPE

    /** LOOP repeats past the end, or slows only-slightly-short media to fit; HOLD freezes on the last frame. */
    @StringAttribute({ choices: Config.ALL_MEDIA_PLAYBACK_MODES })
    @observable
    accessor playbackMode = Config.PLAYBACK_LOOP;

    // animation
    _syncPlaybackToCurrentTime = () => this.syncPlaybackToCurrentTime();

    constructor(...args: ConstructorParameters<typeof MediaAsset>) {
        super(...args);

        this.addReaction(
            reaction(
                () => ({
                    playbackRate: this.playbackRate,
                    videoFramesProvider: this.#loadedVideoFramesProvider,
                }),
                ({ playbackRate, videoFramesProvider }) => {
                    if (videoFramesProvider) {
                        videoFramesProvider.playbackRate = playbackRate;
                    }
                },
                { equals: comparer.shallow, name: 'TimeAwareMediaAsset.providerPlaybackRate' },
            ),
        );

        this.addReaction(
            reaction(
                (): SyncSchedule => ({
                    timeline: this.luminaryParent?._timeline ?? null,
                    times: this.#syncCallbackTimes,
                }),
                (schedule, previousSchedule) =>
                    TimeAwareMediaAsset.#rescheduleSyncCallbacks(
                        this._syncPlaybackToCurrentTime,
                        schedule,
                        previousSchedule,
                    ),
                {
                    equals: TimeAwareMediaAsset.#isSameSyncSchedule,
                    name: 'TimeAwareMediaAsset.syncCallbacks',
                },
            ),
        );

        this.addReaction(
            reaction(
                () => ({
                    heldSourceTime: this.#heldSourceTime,
                    pausedForHold: untracked(() => this.#pausedForHold),
                }),
                ({ pausedForHold }) => {
                    if (pausedForHold) {
                        this.syncPlaybackToCurrentTime();
                    }
                },
                {
                    equals: (a, b) => a.heldSourceTime === b.heldSourceTime,
                    name: 'TimeAwareMediaAsset.heldFrame',
                },
            ),
        );
    }

    /**
     * Here we're overriding the MediaAsset sourceData property so we automatically reset
     * properties which are directly related to the sourceData.
     * - the startTime & endTime
     * - the volume (if the new media no longer has audio)
     */
    override set sourceData(sourceData) {
        super.sourceData = sourceData;

        if (!this.hasAudio) {
            this.volume = 0;
        }

        if (this.#playbackRateOverride !== null) {
            this.#playbackRateOverride = null;
            this.requiresRedraw = true;
        }
    }

    override get sourceData() {
        return super.sourceData;
    }

    set volume(value: number) {
        if (this.mediaAssetType === Config.IMAGE_TYPE_BACKGROUND && value > 0) {
            throw new Error('Cannot set volume for an image.');
        }
        this._audioAsset.volume = value;
    }

    get volume(): number {
        if (this.linkedAsset) {
            return this.linkedAsset.volume;
        }
        return this._audioAsset.volume;
    }

    /**
     * Calculates the relative time for a time aware media asset. This is where we deal with looping and playback
     * rate.
     */
    override timeRelativeTo(time: number) {
        if (!this.isVideo) {
            return time;
        }

        const parentDuration = this.linkToSceneGroup
            ? (this.slide?.sceneGroup?.duration ?? -1)
            : (this.luminaryParent?.duration ?? -1);
        const mediaDuration = this.#mediaDurationMS;
        if (parentDuration === -1 || mediaDuration === -1) {
            return 0;
        }

        const timeMS = Math.min(
            roundToPrecision(time, Config.VIDEO_TIME_PRECISION_MS),
            parentDuration,
        );

        const timeOffsetMS =
            this.playbackMode !== Config.PLAYBACK_HOLD &&
            shouldLoopMedia(parentDuration, mediaDuration, this.volume)
                ? timeMS % mediaDuration
                : timeMS * this.playbackRate;

        const morphOffsetMS = MorphManager.getMediaTimeOffset(this) || 0;
        const sourceTimeMS = this.startTime + timeOffsetMS + morphOffsetMS;
        const heldTimeMS =
            this.playbackMode === Config.PLAYBACK_HOLD
                ? Math.min(sourceTimeMS, this.startTime + this.duration)
                : sourceTimeMS;
        return roundToPrecision(heldTimeMS / 1000, Config.VIDEO_TIME_PRECISION);
    }

    @observable accessor #playbackRateOverride: number | null = null;

    @computed
    get playbackRate(): number {
        const parent = this.luminaryParent;
        if (!this.isVideo || !parent) {
            return 1.0;
        }
        if (this.#playbackRateOverride !== null) {
            return this.#playbackRateOverride;
        }
        if (this.playbackMode === Config.PLAYBACK_HOLD) {
            return 1.0;
        }
        /**
         * Sets the calculated playback rate.
         * If the media is less than 33% shorter than the parent's
         * duration then we're slowing down the video by setting the playbackRate below 1.0. If the media is more than 33%
         * shorter, then we switch to looping and the playbackRate is 1.0.
         */
        return getMediaPlaybackRate(parent.duration, this.duration, this.volume);
    }

    set playbackRate(value: number) {
        if (this.#playbackRateOverride === value) {
            return;
        }
        if (!this.isVideo) {
            return;
        }

        assertNumber(value);
        assertMinimum(value, 0.0625);
        assertMaximum(value, 16);

        this.#playbackRateOverride = value;
        this.requiresRedraw = true;
    }

    get isPlaybackRateOverridden(): boolean {
        return this.#playbackRateOverride !== null;
    }

    _playbackRateOverrideCustomSave() {
        return this.#playbackRateOverride;
    }

    _playbackRateOverrideCustomLoad(value: number) {
        this.#playbackRateOverride = value;
    }

    get #loadedVideoFramesProvider(): VideoFramesProvider | null {
        if (!this.isVideo || !this.luminaryParent || !this.isLoaded) {
            return null;
        }
        return this.videoFramesProvider;
    }

    get #syncCallbackTimes(): readonly number[] {
        const parent = this.luminaryParent;
        if (!parent || !this.isVideo || !this.isLoaded) {
            return [];
        }
        if (parent.duration === -1 || this.duration === -1) {
            return [];
        }
        if (this.playbackMode === Config.PLAYBACK_HOLD) {
            const holdStartMS = Math.max(0, this.#holdStartTimeMS);
            return this.duration > 0 && holdStartMS < parent.duration ? [holdStartMS] : [];
        }
        const mediaDurationMS = this.#mediaDurationMS;
        if (!shouldLoopMedia(parent.duration, mediaDurationMS, this.volume)) {
            return [];
        }
        if (mediaDurationMS <= 0 || parent.duration <= 0) {
            return [];
        }
        const times: number[] = [];
        for (let time = mediaDurationMS; time < parent.duration; time += this.duration) {
            times.push(time);
        }
        return times;
    }

    static #isSameSyncSchedule(a: SyncSchedule, b: SyncSchedule): boolean {
        return a.timeline === b.timeline && comparer.structural(a.times, b.times);
    }

    static #rescheduleSyncCallbacks(
        callback: () => void,
        { timeline, times }: SyncSchedule,
        previousSchedule: SyncSchedule | undefined,
    ) {
        previousSchedule?.timeline?.removeCallback(callback);
        times.forEach((time) => timeline?.addCallback(callback, time));
    }

    get #heldSourceTime(): number | null {
        const parent = this.luminaryParent;
        if (!parent) {
            return null;
        }
        return this.timeRelativeTo(untracked(() => parent.currentTime));
    }

    /**
     * Points the video element at the frame the time mapping says it should be showing, and leaves it
     * playing or paused to match. Registered on the timeline wherever that frame stops following on
     * from the last one: each loop boundary, or the moment a hold reaches its out-point.
     */
    syncPlaybackToCurrentTime() {
        if (!this.luminaryParent || !this.isVideo) {
            return;
        }
        if (this.linkToSceneGroup) {
            return;
        }
        /**
         * cuts are currently only available on voice driven scene groups
         * and they don't allow looping
         */
        if (this.playbackRegions.length > 1) {
            return;
        }
        const videoFramesProvider = this.videoFramesProvider;
        if (!videoFramesProvider) {
            return;
        }
        videoFramesProvider.currentTime = this.timeRelativeTo(this.luminaryParent.currentTime);
        if (this.#isHolding) {
            this.#pausedForHold = true;
            videoFramesProvider.pause();
            return;
        }
        this.#pausedForHold = false;
        if (this.luminaryParent.isPlaying && videoFramesProvider.paused) {
            videoFramesProvider.play().catch((e) => {
                // we should wrap native error explicitly in order to extend error with callstack
                throw new Error(e);
            });
        }
    }

    #pausedForHold = false;

    /** The media's own length measured on its parent's clock, which the playback rate stretches or shrinks. */
    get #mediaDurationMS(): number {
        return this.duration / this.playbackRate;
    }

    /** The parent time at which the mapping first reaches the media's out-point. */
    get #holdStartTimeMS(): number {
        const morphOffsetMS = MorphManager.getMediaTimeOffset(this) || 0;
        return (this.duration - morphOffsetMS) / this.playbackRate;
    }

    get #isHolding(): boolean {
        const parent = this.luminaryParent;
        if (!parent || this.playbackMode !== Config.PLAYBACK_HOLD) {
            return false;
        }
        // the timeline fires a callback off a time rounded up to its own precision, so the parent can
        // still be a fraction short of the out-point when the hold runs
        return (
            this.duration > 0 &&
            parent.currentTime >= this.#holdStartTimeMS - TIMELINE_EVENT_PRECISION
        );
    }

    override _propagatePlay() {
        super._propagatePlay();
        if (this.#isHolding) {
            this.syncPlaybackToCurrentTime();
        }
    }

    override onLoaded() {
        if (this.isLoaded) {
            const initialAutoDraw = this.autoDraw;
            this.autoDraw = false;
            super.onLoaded();
            this.requiresRedraw = true;
            this.autoDraw = initialAutoDraw;
        }
    }

    override destroy(options?: DestroyOptions) {
        const timeline = this.luminaryParent?._timeline;
        super.destroy(options);
        timeline?.removeCallback(this._syncPlaybackToCurrentTime);
        this.clearAnimation();
    }

    override _build() {
        // clear
        this.clearAnimation();
        this.resetTransformation();

        // build
        this._loadSourceWithNetworkManager();
        this._updatePlaybackRegionSources();
        this.resizeMedia();

        super._build();

        // dynamic style
        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this._applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this._applyAnimation();
        }

        // post-process animation
        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override get editableProperties() {
        return super.editableProperties.concat(['volume', 'playbackRateOverride', 'playbackMode']);
    }

    override get morphExcludedProperties(): readonly MorphableProperty[] {
        return ['alpha'];
    }
}
